import { useState, type ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { type RequestedDataGroup } from '../hooks/useRequestedData';
import { useTranslation } from '../i18n/i18n';

type DataTableLayoutProps = {
  groups: RequestedDataGroup[];
  selectedIndex: number;
  onSelectIndex: (index: number) => void;
  /** i18n key for the "issued by" label. Defaults to 'requestedDataModal.issuedBy'. */
  issuedByKey?: string;
  /** Rendering variant. Mobile uses dropdown, desktop uses sidebar tabs. */
  variant: 'mobile' | 'desktop';
  children: (activeGroup: RequestedDataGroup) => ReactNode;
};

/**
 * Shared layout for credential data tables.
 *
 * Desktop: credential sidebar (left) + issuer header + table content (right).
 * Mobile:  credential dropdown + issuer header + table content.
 *
 * Used by RequestedDataModal (Attribute | Description) and
 * AppPresentationPage (Attribute | Value).
 */
export default function DataTableLayout({
  groups,
  selectedIndex,
  onSelectIndex,
  issuedByKey,
  variant,
  children,
}: DataTableLayoutProps) {
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const activeGroup = groups[selectedIndex] ?? groups[0];
  if (!activeGroup) return null;

  if (variant === 'mobile') {
    return (
      <div className="flex flex-1 flex-col gap-3">
        {/* Mobile credential selector – dropdown only when multiple credentials */}
        <div className="relative">
          <button
            type="button"
            onClick={
              groups.length > 1
                ? () => setDropdownOpen(!dropdownOpen)
                : undefined
            }
            className={`flex w-full items-center gap-2 rounded-md border border-input bg-background px-3 py-2 shadow-xs${groups.length <= 1 ? ' cursor-default' : ''}`}
          >
            <span className="flex-1 truncate text-xs text-muted-foreground">
              {activeGroup.name}
            </span>
            {groups.length > 1 && (
              <FontAwesomeIcon
                icon={faChevronDown}
                className={`size-4 shrink-0 text-muted-foreground transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}
              />
            )}
          </button>
          {dropdownOpen && groups.length > 1 && (
            <div className="absolute left-0 right-0 top-full z-10 mt-1 rounded-md border border-border bg-background shadow-lg">
              {groups.map((group, index) => (
                <button
                  key={group.name}
                  type="button"
                  onClick={() => {
                    onSelectIndex(index);
                    setDropdownOpen(false);
                  }}
                  className={`flex w-full items-center px-3 py-2 text-left text-xs transition-colors first:rounded-t-md last:rounded-b-md hover:bg-muted ${
                    index === selectedIndex
                      ? 'font-semibold text-foreground bg-muted'
                      : 'text-muted-foreground'
                  }`}
                >
                  {group.name}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="flex flex-col overflow-hidden rounded-lg border border-border">
          <IssuerHeader group={activeGroup} issuedByKey={issuedByKey} />
          {children(activeGroup)}
        </div>
      </div>
    );
  }

  return (
    <div className="flex overflow-hidden rounded-2xl border border-border">
      {/* Desktop credential sidebar */}
      <div className="flex w-52 shrink-0 flex-col gap-2 self-stretch bg-sidebar p-4">
        {groups.map((group, index) => (
          <button
            key={group.name}
            type="button"
            onClick={() => onSelectIndex(index)}
            className={`flex flex-col gap-1 rounded-md p-3 text-left transition-colors ${
              index === selectedIndex ? 'bg-muted' : 'hover:bg-muted/50'
            }`}
          >
            <span className="text-sm font-semibold text-foreground">
              {group.name}
            </span>
            {group.issuerName && (
              <span className="text-xs text-muted-foreground">
                {group.issuerName}
              </span>
            )}
          </button>
        ))}
      </div>
      {/* Content area */}
      <div className="flex min-w-xs flex-1 flex-col overflow-auto border-y border-r border-border bg-background">
        <IssuerHeader group={activeGroup} issuedByKey={issuedByKey} />
        {children(activeGroup)}
      </div>
    </div>
  );
}

/** Issuer header with optional logo + "Data issued by" + issuer name. */
export function IssuerHeader({
  group,
  issuedByKey,
}: {
  group: RequestedDataGroup;
  issuedByKey?: string;
}) {
  const { t } = useTranslation();
  const issuerName = group.issuerName ?? group.name;
  return (
    <div className="flex items-center gap-2.5 border-b border-border px-4 py-3">
      {group.logo && (
        <div className="flex size-10 shrink-0 items-center overflow-hidden rounded-md bg-white shadow-xs">
          <img
            src={group.logo}
            alt={issuerName}
            className="size-10 object-contain"
          />
        </div>
      )}
      <div className="flex flex-col">
        <span className="text-xs text-muted-foreground">
          {t(issuedByKey ?? 'requestedDataModal.issuedBy')}
        </span>
        <span className="text-sm font-semibold text-card-foreground">
          {issuerName}
        </span>
      </div>
    </div>
  );
}
